// ContactTermineTab.tsx — Kontakt-Tab „Termine" (SP7).
//
// Zeigt die Termine des Kontakts als schlichte Agenda-Liste (Datum-Gruppen,
// Uhrzeit, Betreff, Ort/Teams, Teilnehmer). Datenquelle ist der schlanke Backend-
// Endpoint GET /calendar/contact-termine, der die relevanten Adressen des Kontakts
// (Firma: Zentrale + AP-geschäftlich; Person: geschäftlich + privat) auflöst und
// alle für den User lesbaren Kalender im Zeitraum filtert.
//
// Zeitraum (PO 28.09.2026): ALLE Termine von 3 Jahren zurück bis 2 Jahre voraus,
// ohne Filter. Geladen wird stufenweise (siehe `ladePlan` in agenda.ts): erst
// sofort das nahe Fenster −30/+90 Tage, dann Halbjahr für Halbjahr nach außen,
// höchstens zwei Anfragen gleichzeitig. Die bis dahin geltende Entscheidung 73
// (fix −30/+90) ist damit abgelöst. Vergangene Tage sind dezent abgesetzt.
//
// Reihenfolge (T-308, 13.10.2026: „drehen – neueste immer oben"): der späteste
// Tag steht oben, die Liste beginnt oben — der frühere Sprung auf „heute" ist
// entfallen. Innerhalb eines Tages bleibt der Ablauf früh → spät.
//
// Notiz (T-309, 13.10.2026, PO: „auch auf der Ebene anzeigen"): die Notiz-Seite
// des Termins kommt mit der Liste (`kmvs.notiz`). Ein Auszug steht immer in
// der Zeile, aufgeklappt der ganze Text und „Notiz öffnen".

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import DOMPurify from "dompurify";
import { Badge, Spinner, tokens } from "@fluentui/react-components";
import {
    ArrowClockwise20Regular,
    CalendarLtr20Regular,
    Video16Regular,
    Location16Regular,
    Note16Regular,
    People16Regular,
} from "@fluentui/react-icons";
import { calendarService, ContactTerminEvent } from "../../services/calendarService";
import { useResponsive } from "../../hooks/useResponsive";
import {
    groupTermineByDay,
    formatTerminTime,
    formatDayHeading,
    istVergangen,
    ladePlan,
    mischeTermine,
    notizAuszug,
    type LadeStueck,
} from "./agenda";
import "./contactTermine.css";

interface Props {
    contactId: number;
    /** Aktuell nur informativ (Backend löst Firma/Person selbst auf). */
    isCompany?: boolean;
}

/** Höchstens so viele Nachlade-Anfragen gleichzeitig. */
const PARALLEL = 2;

/** Anzeigename eines Teilnehmers (Name, sonst Adresse). */
function attendeeLabel(a: { name?: string; address?: string }): string {
    const name = (a.name ?? "").trim();
    if (name) return name;
    return (a.address ?? "").trim();
}

/**
 * Notiz-HTML vor dem Einsetzen bereinigen — dieselbe Leitplanke wie auf der
 * Notizseite (`TerminNotizenPage`); der Server bereinigt beim Speichern schon.
 */
function sanitizeNotizHtml(html: string): string {
    const sauber = DOMPurify.sanitize(html ?? "", {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "style"],
        FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "formaction"],
    });
    // Ein Link in der Notiz darf die Akte nicht verlassen: neuer Tab, ohne Opener.
    const vorlage = document.createElement("template");
    vorlage.innerHTML = sauber;
    vorlage.content.querySelectorAll("a[href]").forEach((a) => {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
    });
    return vorlage.innerHTML;
}

/** „Stand 13.10.2026, 15:40" — wann die Notiz zuletzt gespeichert wurde. */
function notizStand(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const datum = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    return `Stand ${datum}, ${zeit}`;
}

/** Tooltip des „über …"-Badges: WARUM steht der Termin in dieser Akte? */
function viaTitel(via: NonNullable<ContactTerminEvent["via"]>): string {
    const name = via.name ?? `Kontakt ${via.contactId}`;
    if (via.ebene === "verbund") return `Termin von ${name} — Firma aus dem Unternehmensverbund`;
    if (via.ebene === "firma") return `Termin von ${name} — Unternehmen dieses Ansprechpartners`;
    if (via.ebene === "ansprechpartner") {
        return via.firmaName
            ? `Termin von ${name} — Ansprechpartner von ${via.firmaName} (Unternehmensverbund)`
            : `Termin von ${name} — Ansprechpartner dieses Unternehmens`;
    }
    return `Termin von ${name} — aus demselben Haushalt`;
}

export const ContactTermineTab: React.FC<Props> = ({ contactId }) => {
    const [events, setEvents] = useState<ContactTerminEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    /** Noch offene Nachlade-Stücke je Richtung (für den Hinweis im Kopf). */
    const [offen, setOffen] = useState<{ zurueck: number; vor: number }>({ zurueck: 0, vor: 0 });
    /** Nachlade-Stücke, die gescheitert sind (Hinweis, keine Fehlermeldung). */
    const [fehlStuecke, setFehlStuecke] = useState(0);
    const { isPhone } = useResponsive();
    const navigate = useNavigate();

    const laufRef = useRef<AbortController | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    /** Sichtbarer Tag vor dem Einmischen — damit ältere Termine die Ansicht nicht wegschieben. */
    const ankerRef = useRef<{ dayKey: string; abstand: number } | null>(null);

    /** Merkt sich den obersten sichtbaren Tag und seinen Abstand zum Rand. */
    const merkeAnker = useCallback(() => {
        const box = scrollRef.current;
        // Ganz oben (oder noch nichts gerendert): nichts festzuhalten.
        if (!box || box.scrollTop <= 0) return;
        const sections = box.querySelectorAll<HTMLElement>("section[data-daykey]");
        for (const sec of Array.from(sections)) {
            if (sec.offsetTop + sec.offsetHeight > box.scrollTop) {
                ankerRef.current = {
                    dayKey: sec.dataset.daykey ?? "",
                    abstand: sec.offsetTop - box.scrollTop,
                };
                return;
            }
        }
    }, []);

    const load = useCallback(async () => {
        laufRef.current?.abort();
        const lauf = new AbortController();
        laufRef.current = lauf;
        ankerRef.current = null;
        setExpandedId(null);
        setFehlStuecke(0);
        if (!contactId) {
            setEvents([]);
            setOffen({ zurueck: 0, vor: 0 });
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        setEvents([]);

        const [nah, ...rest] = ladePlan(new Date());
        setOffen({
            zurueck: rest.filter((s) => s.richtung === "zurueck").length,
            vor: rest.filter((s) => s.richtung === "vor").length,
        });

        // 1) Das nahe Fenster — mit ALLEN Zuweisungen (ohne Zeitgrenze), damit
        //    die nicht jedes Halbjahr erneut einzeln aus Graph geholt werden.
        try {
            const res = await calendarService.listContactTermine(contactId, nah.from, nah.to, {
                zuweisungen: "alle",
                signal: lauf.signal,
            });
            if (lauf.signal.aborted) return;
            setEvents(mischeTermine([], res));
        } catch (err: any) {
            if (lauf.signal.aborted) return;
            setError(err?.message ?? "Fehler beim Laden der Termine");
            setOffen({ zurueck: 0, vor: 0 });
            setLoading(false);
            return;
        }
        setLoading(false);

        // 2) Die Halbjahre, höchstens PARALLEL gleichzeitig, nur Adressfunde.
        //    Ein gescheitertes Stück zählt nur mit — die übrigen laufen weiter.
        const warteschlange: LadeStueck[] = [...rest];
        const arbeiter = async () => {
            for (let stueck = warteschlange.shift(); stueck; stueck = warteschlange.shift()) {
                const richtung = stueck.richtung === "zurueck" ? "zurueck" : "vor";
                try {
                    const res = await calendarService.listContactTermine(
                        contactId,
                        stueck.from,
                        stueck.to,
                        { zuweisungen: "keine", signal: lauf.signal },
                    );
                    if (lauf.signal.aborted) return;
                    if (res.length > 0) {
                        merkeAnker();
                        setEvents((cur) => mischeTermine(cur, res));
                    }
                } catch {
                    if (lauf.signal.aborted) return;
                    setFehlStuecke((n) => n + 1);
                }
                setOffen((cur) => ({ ...cur, [richtung]: Math.max(0, cur[richtung] - 1) }));
            }
        };
        await Promise.all(Array.from({ length: PARALLEL }, () => arbeiter()));
    }, [contactId, merkeAnker]);

    useEffect(() => {
        void load();
        return () => laufRef.current?.abort();
    }, [load]);

    const days = useMemo(() => groupTermineByDay(events), [events]);
    const today = useMemo(() => new Date(), []);

    // Nach dem Einmischen älterer Termine den zuvor sichtbaren Tag an seiner
    // Stelle halten — sonst rutscht die Liste bei jedem Halbjahr nach unten weg.
    useLayoutEffect(() => {
        const box = scrollRef.current;
        const anker = ankerRef.current;
        if (!box || !anker) return;
        const sec = box.querySelector<HTMLElement>(`section[data-daykey="${anker.dayKey}"]`);
        if (sec) box.scrollTop = sec.offsetTop - anker.abstand;
        ankerRef.current = null;
    }, [days]);

    const offenGesamt = offen.zurueck + offen.vor;

    return (
        <div
            ref={scrollRef}
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowY: "auto",
                position: "relative",
            }}
        >
            {/* ── Kopfleiste ─────────────────────────────────────────────── */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                    padding: "14px 20px 12px",
                    backgroundColor: tokens.colorBrandBackground2,
                    borderTop: `3px solid ${tokens.colorBrandStroke1}`,
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={{ display: "inline-flex", color: tokens.colorBrandForeground1 }}>
                        <CalendarLtr20Regular />
                    </span>
                    <span
                        style={{
                            fontWeight: tokens.fontWeightSemibold,
                            fontSize: tokens.fontSizeBase400,
                            color: tokens.colorBrandForeground2,
                        }}
                    >
                        Termine
                    </span>
                    <Badge appearance="filled" color="brand" size="medium">
                        {events.length}
                    </Badge>
                    {!loading && offenGesamt > 0 && (
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: tokens.fontSizeBase200,
                                color: tokens.colorNeutralForeground3,
                            }}
                        >
                            <Spinner size="extra-tiny" />
                            {offen.zurueck > 0 ? "lädt ältere Termine…" : "lädt spätere Termine…"}
                        </span>
                    )}
                    {!loading && offenGesamt === 0 && fehlStuecke > 0 && (
                        <span
                            title="Einzelne Zeiträume konnten nicht geladen werden. Mit „Aktualisieren“ erneut versuchen."
                            style={{
                                fontSize: tokens.fontSizeBase200,
                                color: tokens.colorNeutralForeground3,
                            }}
                        >
                            nicht alle Zeiträume geladen
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => void load()}
                    title="Termine aktualisieren"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: tokens.colorNeutralForeground2,
                        minWidth: isPhone ? 44 : undefined,
                        minHeight: isPhone ? 44 : undefined,
                        padding: 4,
                        flexShrink: 0,
                    }}
                >
                    <ArrowClockwise20Regular />
                </button>
            </div>

            {/* ── Inhalt: Lade-/Fehler-/Leer-/Agenda-Zustand ─────────────── */}
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                    <Spinner size="tiny" label="Termine werden geladen…" />
                </div>
            ) : error ? (
                <div
                    style={{
                        padding: 24,
                        textAlign: "center",
                        color: tokens.colorStatusDangerForeground1,
                        fontSize: tokens.fontSizeBase300,
                    }}
                >
                    {error}
                </div>
            ) : days.length === 0 && offenGesamt > 0 ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                    <Spinner size="tiny" label="Ältere und spätere Termine werden gesucht…" />
                </div>
            ) : days.length === 0 ? (
                <div
                    style={{
                        padding: "40px 24px",
                        textAlign: "center",
                        color: tokens.colorNeutralForeground3,
                        fontSize: tokens.fontSizeBase300,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <CalendarLtr20Regular style={{ opacity: 0.5, width: 28, height: 28 }} />
                    <div>Keine Termine zu diesem Kontakt (drei Jahre zurück bis zwei Jahre voraus).</div>
                    <div style={{ fontSize: tokens.fontSizeBase200 }}>
                        Termine erscheinen, sobald eine E-Mail-Adresse des Kontakts als
                        Teilnehmer in einem für Sie lesbaren Kalender vorkommt — oder sobald
                        ihm ein Termin ausdrücklich zugewiesen wurde.
                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", padding: "8px 0 24px" }}>
                    {days.map((day) => {
                        const past = istVergangen(day.date, today);
                        return (
                            <section key={day.dayKey} data-daykey={day.dayKey}>
                                {/* Tages-Überschrift */}
                                <div
                                    style={{
                                        position: "sticky",
                                        top: 52,
                                        zIndex: 1,
                                        padding: "8px 20px 6px",
                                        backgroundColor: tokens.colorNeutralBackground1,
                                        fontSize: tokens.fontSizeBase200,
                                        fontWeight: tokens.fontWeightSemibold,
                                        letterSpacing: 0.2,
                                        textTransform: "uppercase",
                                        color: past
                                            ? tokens.colorNeutralForeground4
                                            : tokens.colorBrandForeground2,
                                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                                    }}
                                >
                                    {formatDayHeading(day.date, today)}
                                </div>

                                {/* Termine des Tages */}
                                {day.events.map((ev) => {
                                    const expanded = expandedId === ev.id;
                                    const realAttendees = ev.attendees.filter((a) => a.type !== "resource");
                                    const shown = realAttendees.slice(0, isPhone ? 2 : 4);
                                    const rest = realAttendees.length - shown.length;
                                    const notiz = ev.kmvs?.notiz ?? null;
                                    const auszug = notizAuszug(ev);
                                    const terminId = ev.kmvs?.terminId ?? null;
                                    return (
                                        <div
                                            key={`${ev.source.address}:${ev.id}`}
                                            data-termin-zeile
                                            onClick={() =>
                                                setExpandedId((cur) => (cur === ev.id ? null : ev.id))
                                            }
                                            style={{
                                                display: "flex",
                                                gap: 12,
                                                padding: "12px 20px",
                                                cursor: "pointer",
                                                borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                                                opacity: past ? 0.72 : 1,
                                                backgroundColor: expanded
                                                    ? tokens.colorNeutralBackground1Selected
                                                    : "transparent",
                                            }}
                                        >
                                            {/* Uhrzeit-Spalte */}
                                            <div
                                                style={{
                                                    flexShrink: 0,
                                                    width: 96,
                                                    fontSize: tokens.fontSizeBase200,
                                                    fontWeight: tokens.fontWeightSemibold,
                                                    color: tokens.colorNeutralForeground2,
                                                    paddingTop: 2,
                                                }}
                                            >
                                                {formatTerminTime(ev)}
                                            </div>

                                            {/* Betreff + Meta */}
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "baseline",
                                                        gap: 8,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: tokens.fontSizeBase300,
                                                            fontWeight: tokens.fontWeightSemibold,
                                                            color: tokens.colorNeutralForeground1,
                                                            whiteSpace: expanded ? "normal" : "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}
                                                    >
                                                        {ev.subject || "(ohne Betreff)"}
                                                    </span>
                                                    {/* „über <Firma>" — der Termin gehört einem
                                                        anderen Kontakt des Kreises: einer
                                                        Verbundfirma, dem Unternehmen des
                                                        Ansprechpartners oder dem Haushalt. Gleiche
                                                        Sprache wie im Vorgänge-Reiter. */}
                                                    {ev.via && (
                                                        <Badge
                                                            appearance="outline"
                                                            size="small"
                                                            title={viaTitel(ev.via)}
                                                            style={{ flexShrink: 0 }}
                                                        >
                                                            über{" "}
                                                            {ev.via.name ?? `Kontakt ${ev.via.contactId}`}
                                                        </Badge>
                                                    )}
                                                    {/* Ausdrücklich zugewiesen — im Unterschied zu
                                                        einem, der nur über die Teilnehmeradressen
                                                        gefunden wurde. */}
                                                    {ev.zugewiesen && (
                                                        <Badge
                                                            appearance="tint"
                                                            color="brand"
                                                            size="small"
                                                            title="Dieser Termin wurde dem Kontakt ausdrücklich zugewiesen."
                                                            style={{ flexShrink: 0 }}
                                                        >
                                                            zugewiesen
                                                        </Badge>
                                                    )}
                                                </div>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        alignItems: "center",
                                                        gap: 8,
                                                        marginTop: 4,
                                                        fontSize: tokens.fontSizeBase200,
                                                        color: tokens.colorNeutralForeground3,
                                                    }}
                                                >
                                                    {ev.isOnlineMeeting && (
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 3,
                                                                color: tokens.colorBrandForeground2,
                                                            }}
                                                        >
                                                            <Video16Regular /> Teams
                                                        </span>
                                                    )}
                                                    {ev.location && (
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 3,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Location16Regular />
                                                            <span
                                                                style={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    maxWidth: 220,
                                                                }}
                                                            >
                                                                {ev.location}
                                                            </span>
                                                        </span>
                                                    )}
                                                    {realAttendees.length > 0 && (
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 3,
                                                            }}
                                                        >
                                                            <People16Regular />
                                                            {shown.map((a) => attendeeLabel(a)).join(", ")}
                                                            {rest > 0 ? ` +${rest}` : ""}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* T-309: Notiz-Auszug — ohne Aufklappen sichtbar. */}
                                                {auszug && !expanded && (
                                                    <div
                                                        data-notiz-auszug
                                                        title="Notiz zum Termin — zum Lesen aufklappen"
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 4,
                                                            marginTop: 4,
                                                            minWidth: 0,
                                                            fontSize: tokens.fontSizeBase200,
                                                            color: tokens.colorNeutralForeground2,
                                                        }}
                                                    >
                                                        <Note16Regular
                                                            style={{ flexShrink: 0, color: tokens.colorBrandForeground2 }}
                                                        />
                                                        <span
                                                            style={{
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            {auszug}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* T-309: die ganze Notiz (nur ausgeklappt). */}
                                                {expanded && notiz && (
                                                    <div
                                                        className="kvp-termin-notiz"
                                                        // Markieren oder einen Link anklicken klappt die Zeile nicht zu.
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            marginTop: 8,
                                                            padding: "8px 10px",
                                                            borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
                                                            backgroundColor: tokens.colorNeutralBackground2,
                                                            borderRadius: 4,
                                                            fontSize: tokens.fontSizeBase200,
                                                            color: tokens.colorNeutralForeground1,
                                                            cursor: "auto",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 6,
                                                                flexWrap: "wrap",
                                                                color: tokens.colorNeutralForeground3,
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            <Note16Regular style={{ color: tokens.colorBrandForeground2 }} />
                                                            <span style={{ fontWeight: tokens.fontWeightSemibold }}>Notiz</span>
                                                            {notizStand(notiz.updatedAt) && (
                                                                <span>· {notizStand(notiz.updatedAt)}</span>
                                                            )}
                                                        </div>
                                                        {notiz.contentHtml ? (
                                                            <div
                                                                data-termin-notiz
                                                                className="kvp-termin-notiz__text"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: sanitizeNotizHtml(notiz.contentHtml),
                                                                }}
                                                            />
                                                        ) : null}
                                                        {notiz.hatZeichnung && (
                                                            <div style={{ color: tokens.colorNeutralForeground3, marginTop: 4 }}>
                                                                {notiz.contentHtml
                                                                    ? "Dazu eine handschriftliche Zeichnung."
                                                                    : "Handschriftliche Notiz (Zeichnung)"}
                                                            </div>
                                                        )}
                                                        {terminId != null && (
                                                            <button
                                                                type="button"
                                                                className="kvp-termin-notiz__oeffnen"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/termine/notizen/${terminId}`);
                                                                }}
                                                                style={{
                                                                    marginTop: 6,
                                                                    minHeight: isPhone ? 44 : undefined,
                                                                }}
                                                            >
                                                                Notiz öffnen
                                                            </button>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Quelle-Kalender + Vorschau (nur ausgeklappt) */}
                                                {expanded && (
                                                    <div
                                                        style={{
                                                            marginTop: 8,
                                                            fontSize: tokens.fontSizeBase200,
                                                            color: tokens.colorNeutralForeground3,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: 6,
                                                        }}
                                                    >
                                                        {ev.bodyPreview && (
                                                            <div style={{ color: tokens.colorNeutralForeground2 }}>
                                                                {ev.bodyPreview}
                                                            </div>
                                                        )}
                                                        <div>
                                                            Kalender: {ev.source.displayName || ev.source.address}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ContactTermineTab;
